'use client'

import { useGlobal, useTranslation, useUtilityContext } from "@theme/base/contexts"
import { useUserProfile } from "@theme/base/contexts"
import { Box, Typography, Button, Stack, IconButton } from "@mui/material"
import { AppLink, AppTable } from "@ui/components"
import { IColumnInfo } from '@ui/components/molecules/AppTable'
import { TDeliveryNoteSearchResponse, TAddress } from "@schemas/graphql"
import { DateTimeFormatsEnum } from '@constants'
import { getDeliveryNoteUrl } from '@lib/app/appUrls'
import moment from "moment"
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import { useFormikContext } from "formik"
import { useRemote } from "@hooks"
import { getDeliveryNoteDocumentGraphQL } from "@logics"
import { UUID } from "crypto"

const addressToText = (address: TAddress) => {
    return `${address.streetAddress} ${address.num}  ${address.city} ${address.province} ${address.zipCode} (${address.countryIsoId})`
}

interface IDeliveryNotesTableProps {
    deliveryNotes: TDeliveryNoteSearchResponse[]
}

const DeliveryNotesList = ({ deliveryNotes }: IDeliveryNotesTableProps) => {
    const { getTranslation } = useTranslation()
    const { isSales } = useUserProfile()
    const { isSubmitting } = useFormikContext()
    const { fetchRemote } = useRemote()
    const { formatDate, moneyFormatCurrency } = useUserProfile()

    const handleTransportClick = (url: string) => {
        window.open(url, '_blank')
    }

    function renderDeliveryDownload(invoice: TDeliveryNoteSearchResponse) {
        const { downloadFile } = useUtilityContext()

        const handleClick = () => {
            fetchRemote(async () => getDeliveryNoteDocumentGraphQL(invoice!.id as UUID))
                .then(response => downloadFile(response!.toString()))
        }

        return (
            <IconButton onClick={handleClick}>
                <SimCardDownloadOutlinedIcon />
            </IconButton>
        )
    }

    const genericColumns: IColumnInfo<TDeliveryNoteSearchResponse>[] = [
        {
            label: getTranslation('deliverynotes.ref'),
            cellRenderer: ((dato: TDeliveryNoteSearchResponse) => (
                <AppLink href={getDeliveryNoteUrl(dato.id)}>{dato.number}</AppLink>
            ))
        },
        {
            label: getTranslation('deliverynotes.deliverydate'),
            cellRenderer: ((dato: TDeliveryNoteSearchResponse) => (
                <Typography variant="size14">{formatDate(dato.deliveryDate, DateTimeFormatsEnum.DateSlash)}</Typography>
            ))
        },
        {
            label: getTranslation('deliverynotes.isinvoiced'),
            cellRenderer: ((dato: TDeliveryNoteSearchResponse) => (
                <Stack alignItems="center">
                    <Box
                        width="35px"
                        height="35px"
                        sx={{
                            backgroundColor: dato.isInvoiced ? 'success.main' : 'orange.main',
                            borderRadius: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Typography color="white.main" variant="size12" fontWeight="500">{dato.isInvoiced ? getTranslation('list.result.si') : getTranslation('list.result.no')}</Typography>
                    </Box>
                </Stack>
            )),
        }, {
            label: getTranslation('deliverynotes.amount'),
            sx: { textAlign: 'right' },
            cellRenderer: ((dato: TDeliveryNoteSearchResponse) => (
                <Typography variant="size14" color="primary" fontWeight="500">
                    {moneyFormatCurrency({ code: dato.totals.totalAmount.code, value: dato.totals.totalAmount.value })}
                </Typography>
            )),

        },
        {
            label: getTranslation('deliverynotes.address'),
            cellRenderer: ((dato: TDeliveryNoteSearchResponse) => (
                <Typography variant="size12">{addressToText(dato.shippingAddress)}</Typography>
            ))
        },
        {
            label: getTranslation('deliverynotes.trackingnumber'),
            cellRenderer: ((dato: TDeliveryNoteSearchResponse) => (
                <>
                    {dato.transportUrlTrack && (
                        <Button
                            sx={{ width: { xs: '100%', lg: '200px' } }}
                            variant='contained'
                            startIcon={<LocalShippingOutlinedIcon fontSize="small" />}
                            onClick={() => handleTransportClick(dato.transportUrlTrack!)}
                        >
                            <Typography variant='size14'>Tracking</Typography>
                        </Button>
                    )}
                </>
            ))
        },
        { label: getTranslation('invoices.table.download'), cellRenderer: renderDeliveryDownload }
    ]

    const agentColumns = [
        {
            label: getTranslation('deliverynotes.client'),
            cellRenderer: ((dato: TDeliveryNoteSearchResponse) => (
                <Typography variant="size12">{dato.clientName ?? '-'}</Typography>
            ))
        },
    ]

    const columns = !isSales ? genericColumns : [
        ...agentColumns,
        ...genericColumns
    ]

    return (
        <AppTable
            rows={deliveryNotes}
            columns={columns}
            noResultsText={getTranslation('common.table.noresults')}
            loading={isSubmitting}
        />
    )
}

export default DeliveryNotesList